import {BasePageComponent} from "./base-page.component";
import {ScreenService} from "../services/screen.service";
import {LocalizerService} from "../services/localizer.service";
import {Directive, inject, Input, OnDestroy} from "@angular/core";
import {FarfisaDevice} from "../models/devices/farfisa_device";
import {combineLatest, map, Observable, take, takeUntil} from "rxjs";
import {DeviceShadow, DeviceStatus} from "../models/shadow/device-shadow";
import {IotService} from "../services/iot.service";
import {CallUpdate} from "../models/webrtc/call/v2/call-update";
import {CallEntity, CallEntityKind, CallParticipant} from "../models/webrtc/call/v2/call-entity";
import {DeviceOutput} from "../../devices/models/device_output";
import {CallSessionManager} from "../services/call-session-manager.service";
import {Session} from "../models/webrtc/call/call";
import {Versioning} from "../utils/versioning";
import {v2CallVersion} from "../constants";
import {PageTitleService} from "../services/page-title.service";
import {Store} from "@ngrx/store";
import {CurrentCallActions} from "../../state/call/current-call.actions";
import {selectCurrentCall} from "../../state/call/current-call.selector";
import {CallLeg} from "../../state/call/current-call-state";

@Directive()
export abstract class BaseDashboardComponent extends BasePageComponent implements OnDestroy {

    @Input() installationId: string | null = null;

    private pageTitleService = inject(PageTitleService);
    devices: FarfisaDevice[] = [];
    shadowUpdates: DashboardDeviceShadow[] = [];

    incomingCallCardVisible: boolean = false;
    incomingCall: CallUpdate | null = null;

    currentCallingEntity: CallEntity | null = null;
    currentCallingEntityName: string | null = null;
    currentCallingEntityOutputs: DeviceOutput[] | undefined = undefined;
    currentCallingEntityThingName: string | null = null;

    protected constructor(screen: ScreenService, localizer: LocalizerService,
        protected callSessionManager: CallSessionManager, protected iot: IotService,
        protected store: Store) {
        super(screen, localizer);
    }

    ngOnDestroy(): void {
        this.pageTitleService.setTitle(null);
        this.onDestroy.next();
        this.onDestroy.complete();
    }

    checkForAnsweringCallSession(entityId: string) {
        const session = this.callSessionManager.getIncomingAnsweringCallSession(entityId!);
        if (session) {
            this.viewIncomingCall(session, true);
            this.callSessionManager.setIncomingAnsweringCallSession(null);
        }

        this.callSessionManager.answeringCallSession(entityId)?.pipe(
            takeUntil(this.onDestroy)
        ).subscribe(session => {
            this.viewIncomingCall(session, true);
            this.callSessionManager.setIncomingAnsweringCallSession(null);
        });
    }

    protected abstract viewIncomingCall(session: Session, answer: boolean): void;

    protected isDeviceReady = (device: FarfisaDevice): boolean => this.isDeviceInState(device, DeviceStatus.Ready);

    protected isDeviceBusy = (device: FarfisaDevice): boolean => this.isDeviceInState(device, DeviceStatus.Busy);

    protected shouldShowLiveViewButton(device: FarfisaDevice): boolean {
        return !this.isDeviceBusy(device);
    }

    protected shouldShowBusyButton(device: FarfisaDevice): boolean {
        return this.isDeviceBusy(device);
    }

    protected subscribeToDeviceShadows() {
        const deviceShadowObservables = this.devices
            .filter(device => device.thing_name)
            .map(device => {
                const shadowObservable = this.iot.getDeviceShadow(device.thing_name);
                if (shadowObservable) {
                    return shadowObservable.pipe(
                        map(shadow => ({
                            thingName: device.thing_name,
                            shadow
                        } as DashboardDeviceShadow))
                    );
                }
                return null;
            })
            .filter(obs => obs !== null) as Array<Observable<DashboardDeviceShadow>>;

        if (deviceShadowObservables.length > 0) {
            combineLatest(deviceShadowObservables).pipe(
                takeUntil(this.onDestroy)
            ).subscribe(shadowUpdates => {
                this.shadowUpdates = shadowUpdates;
                for (const update of shadowUpdates) {
                    const device = this.devices.find(d => d.thing_name === update.thingName);
                    if (device) {
                        device.status = update.shadow;
                    }
                }
            });
        }
    }

    protected rejectIncomingCall(callId: string) {
        this.callSessionManager.closeMainLeg(callId);
        this.incomingCallSessionEnded();
    }

    protected incomingCallSessionEnded() {
        this.incomingCallCardVisible = false;
        this.incomingCall = null;
    }

    private isDeviceInState(device: FarfisaDevice, state: DeviceStatus): boolean {
        if (!device?.thing_name) {
            return false;
        }

        const deviceStatus = device.status?.status || DeviceStatus.Offline;
        return deviceStatus === state;
    }

    //#region Call Management

    /**
     * Check if device supports V2 call protocol from shadow by thingName
     */
    protected isDeviceV2(thingName: string | null | undefined): boolean {
        if (!thingName || this.shadowUpdates.length === 0) {
            return false;
        }

        const shadow = this.shadowUpdates.find(u => u.thingName === thingName)?.shadow;
        const applicationVersion = shadow?.device?.version?.application;

        if (!applicationVersion) return false;
        return new Versioning(applicationVersion).compareTo(v2CallVersion) >= 0;
    }

    /**
     * Build the caller CallParticipant for this dashboard.
     * Must be implemented by subclasses (PBX or Unit dashboard).
     */
    protected abstract buildCaller(): CallParticipant;

    /**
     * Enrich Redux call state with local dashboard data (name, outputs, thingName)
     * when entity info is missing (e.g. incoming calls only carry id+kind from MQTT).
     * Handles doorstations from this.devices. Subclasses can override to handle
     * additional entity kinds (e.g. units in PBX dashboard).
     */
    protected enrichLegEntityInfo(callId: string, leg: CallLeg | undefined | null): void {
        if (!callId || !leg || leg.entityName) return;

        const sessionId = leg.sessions[0]?.sessionId;
        if (!sessionId) return;

        const entity = leg.entity;
        if (!entity || entity.kind !== CallEntityKind.Doorstation) return;

        const device = this.devices.find(d => d.id === entity.id);
        if (device?.name) {
            this.store.dispatch(CurrentCallActions.updateSessionEntityInfo({
                callId,
                sessionId,
                entityName: device.name,
                entityOutputs: device.available_outputs,
                entityThingName: device.thing_name,
                // Only set entityIsV2 if not already defined (preserve value from MQTT notification)
                entityIsV2: leg.entityIsV2 ?? this.isDeviceV2(device.thing_name),
            }));
        }
    }

    /**
     * After devices are loaded, try to enrich any active call that was missing
     * entity info (e.g. incoming call arrived before dashboard API completed).
     */
    protected enrichActiveCallFromDevices(): void {
        this.store.select(selectCurrentCall).pipe(
            take(1)
        ).subscribe(state => {
            if (!state?.callId) return;
            this.enrichLegEntityInfo(state.callId, state.mainLeg);
            this.enrichLegEntityInfo(state.callId, state.pipLeg);
        });
    }

    /**
     * Start an outgoing call to a callee.
     */
    protected startOutgoingCall(callee: CallParticipant): void {
        const calleeWithIsV2: CallParticipant = {
            ...callee,
            isV2: callee.isV2 ?? this.isDeviceV2(callee.thingName)
        };

        this.callSessionManager.startCall(
            this.buildCaller(),
            calleeWithIsV2,
            this.installationId!
        );
    }

    //#endregion Call Management
}

export interface DashboardDeviceShadow {

    thingName: string;

    shadow: DeviceShadow;

}
